import { create } from 'zustand';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName?: string | null;
}

export type SyncStatus = 'idle' | 'syncing' | 'synced' | 'offline' | 'error';

export interface AuthStoreState {
  user: AuthUser | null;
  encryptionKey: CryptoKey | null;
  encryptionSalt: string | null;
  isAuthenticated: boolean;
  authReady: boolean;
  needsEncryptionPassword: boolean;
  syncStatus: SyncStatus;
  lastSyncedAt: string | null;
  syncError: string | null;
  setUser: (user: AuthUser | null) => void;
  setEncryptionKey: (key: CryptoKey | null, salt?: string | null) => void;
  setNeedsEncryptionPassword: (needs: boolean) => void;
  setAuthReady: (ready: boolean) => void;
  setSyncStatus: (status: SyncStatus, error?: string | null) => void;
  markSynced: () => void;
  reset: () => void;
}

export const useAuthStore = create<AuthStoreState>((set) => ({
  user: null,
  encryptionKey: null,
  encryptionSalt: null,
  isAuthenticated: false,
  authReady: false,
  needsEncryptionPassword: false,
  syncStatus: 'idle',
  lastSyncedAt: null,
  syncError: null,
  setUser: (user) =>
    set({
      user,
      isAuthenticated: user !== null,
      needsEncryptionPassword: user !== null,
    }),
  setEncryptionKey: (encryptionKey, salt) =>
    set((state) => ({
      encryptionKey,
      encryptionSalt: salt ?? state.encryptionSalt,
      needsEncryptionPassword: encryptionKey === null && state.user !== null,
    })),
  setNeedsEncryptionPassword: (needsEncryptionPassword) => set({ needsEncryptionPassword }),
  setAuthReady: (authReady) => set({ authReady }),
  setSyncStatus: (syncStatus, syncError = null) => set({ syncStatus, syncError }),
  markSynced: () =>
    set({
      syncStatus: 'synced',
      lastSyncedAt: new Date().toISOString(),
      syncError: null,
    }),
  reset: () =>
    set({
      user: null,
      encryptionKey: null,
      encryptionSalt: null,
      isAuthenticated: false,
      needsEncryptionPassword: false,
      syncStatus: 'idle',
      lastSyncedAt: null,
      syncError: null,
    }),
}));
